import React from "react";
import "../ModernToggleSwitch.css";
import "./SettingsList.css";

/**
 * Grouped settings list, as in the mobile app's settings screens:
 * captioned cards of rows with an icon tile, label, optional sub-line, value,
 * switch or chevron. Used by the personal and the company settings.
 */

/** A captioned card. `note` is a muted line under the card. */
export function SettingsSection({ caption, note, children, className = "" }) {
  return (
    <section className={`kj-settings-section${className ? ` ${className}` : ""}`}>
      {caption ? <h3 className="kj-settings-caption">{caption}</h3> : null}
      <div className="kj-settings-card">{children}</div>
      {note ? <p className="kj-settings-note">{note}</p> : null}
    </section>
  );
}

/** Muted text block outside a card (intros, hints). */
export function SettingsNote({ children, className = "" }) {
  return <p className={`kj-settings-note${className ? ` ${className}` : ""}`}>{children}</p>;
}

/**
 * One row.
 *   icon       Material Symbols name for the tile
 *   label, sub
 *   value      right-aligned current value
 *   onClick    makes the row a button (chevron right, or down with `expanded`)
 *   expanded   the row opens a panel: chevron down, rotated while open, and
 *              `children` render under the row
 *   toggle     { checked, onChange(next), disabled } — the whole row toggles
 *   danger     red tile and label, no chevron
 *   dimmed     45% (locked rows); still clickable
 *   busy       spinner in place of the chevron
 *   trailing   custom node before the chevron
 *   leading    custom node in place of the icon tile (e.g. an avatar)
 */
export function SettingsRow({
  icon,
  label,
  sub,
  value,
  onClick,
  expanded,
  toggle,
  danger = false,
  dimmed = false,
  busy = false,
  trailing = null,
  chevron,
  children,
  className = "",
  id,
  title,
  leading = null,
}) {
  const expandable = expanded !== undefined;
  const showChevron = chevron !== undefined ? chevron : !!onClick && !toggle && !danger;
  const rowClass = [
    "kj-settings-row",
    danger ? "is-danger" : "",
    dimmed ? "is-dimmed" : "",
    expanded ? "is-expanded" : "",
    sub ? "has-sub" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      {leading ? (
        <span className="kj-settings-row__leading">{leading}</span>
      ) : icon ? (
        <span className="kj-settings-row__tile" aria-hidden="true">
          <span className="material-symbols-outlined">{icon}</span>
        </span>
      ) : null}
      <span className="kj-settings-row__text">
        <span className="kj-settings-row__label">{label}</span>
        {sub ? <span className="kj-settings-row__sub">{sub}</span> : null}
      </span>
      {value !== undefined && value !== null && value !== "" ? (
        <span className="kj-settings-row__value">{value}</span>
      ) : null}
      {trailing}
      {toggle ? (
        <span className={`modern-toggle-switch modern-toggle-orange kj-settings-row__switch${toggle.disabled ? " disabled" : ""}`}>
          <input
            type="checkbox"
            checked={!!toggle.checked}
            disabled={!!toggle.disabled}
            onChange={(e) => toggle.onChange && toggle.onChange(e.target.checked)}
          />
          <span className="modern-toggle-slider"></span>
        </span>
      ) : null}
      {busy ? (
        <span className="kj-settings-row__spinner" aria-hidden="true" />
      ) : showChevron ? (
        <span className="material-symbols-outlined kj-settings-row__chevron" aria-hidden="true">
          {expandable ? "keyboard_arrow_down" : "keyboard_arrow_right"}
        </span>
      ) : null}
    </>
  );

  let row;
  if (toggle) {
    // A label, so a click anywhere on the row flips the switch.
    row = (
      <label className={`${rowClass} is-toggle`} id={id} title={title}>
        {body}
      </label>
    );
  } else if (onClick) {
    row = (
      <button
        type="button"
        className={rowClass}
        onClick={onClick}
        aria-expanded={expandable ? !!expanded : undefined}
        id={id}
        title={title}
      >
        {body}
      </button>
    );
  } else {
    row = (
      <div className={rowClass} id={id} title={title}>
        {body}
      </div>
    );
  }

  return (
    <div className={`kj-settings-item${expanded ? " is-expanded" : ""}`}>
      {row}
      {expandable && expanded && children ? <div className="kj-settings-panel">{children}</div> : null}
    </div>
  );
}

/** Page column: mobile-like width on the left, 16px between blocks. */
export function SettingsStack({ children, className = "" }) {
  return <div className={`kj-settings-stack${className ? ` ${className}` : ""}`}>{children}</div>;
}

/**
 * Tinted banner (mobile Pro `Banner`): filled icon, title in the tone colour,
 * optional body text and actions under it.
 *   tone  ok | warn | err | info | primary
 */
export function SettingsBanner({ tone = "ok", icon = "check_circle", title, body, children, className = "" }) {
  const top = !!(body || children);
  return (
    <div className={`kj-settings-banner is-${tone}${top ? " is-top" : ""}${className ? ` ${className}` : ""}`}>
      <span className="material-symbols-outlined kj-settings-banner__icon" aria-hidden="true">{icon}</span>
      <div className="kj-settings-banner__text">
        {title ? <span className="kj-settings-banner__title">{title}</span> : null}
        {body ? <span className="kj-settings-banner__body">{body}</span> : null}
        {children ? <div className="kj-settings-banner__actions">{children}</div> : null}
      </div>
    </div>
  );
}

/** Small status tag (mobile Pro `ProTag`). tone: ok | warn | err | muted */
export function SettingsTag({ tone = "muted", children, className = "" }) {
  return <span className={`kj-settings-tag is-${tone}${className ? ` ${className}` : ""}`}>{children}</span>;
}
